import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { fn } from '../api/functions';
import { 
  PlayIcon, 
  PauseIcon, 
  PencilIcon, 
  CheckIcon, 
  XMarkIcon, 
  SparklesIcon, 
  ArrowUturnLeftIcon, 
  MicrophoneIcon,
  ForwardIcon,
  BackwardIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/solid';
import PhotoUpload from './PhotoUpload';
import { useAuth } from '../contexts/AuthContext';
import { doc, runTransaction, collection, query, orderBy, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export const AnswerThread = ({ storyId, questionId, question, refreshTrigger = 0, onReRecord, userAccessLevel = 'owner' }) => {
  const { currentUser } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [editingAnswer, setEditingAnswer] = useState(null);
  const [editText, setEditText] = useState('');
  const [originalEditText, setOriginalEditText] = useState(''); // Store text before AI cleanup
  const [saving, setSaving] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [generatedFollowup, setGeneratedFollowup] = useState(null);
  const [audioProgress, setAudioProgress] = useState({});
  const [audioDuration, setAudioDuration] = useState({});
  const [audioLoading, setAudioLoading] = useState({});
  const [photos, setPhotos] = useState({});
  const [showPhotoModal, setShowPhotoModal] = useState(null);
  const audioRefs = React.useRef({});

  // Calculate dynamic textarea height based on content length
  const calculateTextareaHeight = (text) => {
    if (!text) return 'min-h-[100px]';
    
    const lineCount = text.split('\n').length;
    const charCount = text.length;
    
    // Calculate based on both line breaks and character length
    // More generous height calculations for better UX
    if (lineCount > 10 || charCount > 1200) {
      return 'min-h-[400px]';
    } else if (lineCount > 7 || charCount > 800) {
      return 'min-h-[300px]';
    } else if (lineCount > 4 || charCount > 400) {
      return 'min-h-[200px]';
    } else if (lineCount > 2 || charCount > 150) {
      return 'min-h-[150px]';
    }
    return 'min-h-[100px]';
  };

  useEffect(() => {
    if (!storyId || !questionId) return;

    const fetchAnswerThread = async () => {
      try {
        setLoading(true);
        const result = await fn.getQuestionAnswerThread({ storyId, questionId });
        
        const threads = result.data.threads || [];
        setThreads(threads);
        
        // Load photos for all answers
        const photosData = {};
        threads.forEach(thread => {
          if (thread.originalAnswer?.photos) {
            photosData[thread.originalAnswer.id] = thread.originalAnswer.photos;
          }
          thread.followups?.forEach(followup => {
            if (followup.photos) {
              photosData[followup.id] = followup.photos;
            }
          });
        });
        setPhotos(photosData);
      } catch (error) {
        console.error('Error fetching answer thread:', error);
        setThreads([]);
        setPhotos({});
      } finally {
        setLoading(false);
      }
    };

    fetchAnswerThread();
  }, [storyId, questionId, refreshTrigger]);

  const handlePlayPause = async (audioUrl, answerId) => {
    if (playingAudio === answerId) {
      // Pause current audio
      const audioElement = audioRefs.current[answerId];
      if (audioElement) {
        audioElement.pause();
        setPlayingAudio(null);
      }
    } else {
      // Stop any currently playing audio
      if (playingAudio) {
        const currentAudio = audioRefs.current[playingAudio];
        if (currentAudio) {
          currentAudio.pause();
        }
      }

      // Set loading state
      setAudioLoading(prev => ({ ...prev, [answerId]: true }));

      // Create new audio element if it doesn't exist
      if (!audioRefs.current[answerId]) {
        const audioElement = new Audio(audioUrl);
        audioRefs.current[answerId] = audioElement;

        // Event listeners for audio progress and metadata
        audioElement.addEventListener('loadedmetadata', () => {
          setAudioDuration(prev => ({ ...prev, [answerId]: audioElement.duration }));
          setAudioLoading(prev => ({ ...prev, [answerId]: false }));
        });

        audioElement.addEventListener('timeupdate', () => {
          setAudioProgress(prev => ({ 
            ...prev, 
            [answerId]: {
              currentTime: audioElement.currentTime,
              percentage: (audioElement.currentTime / audioElement.duration) * 100
            }
          }));
        });

        audioElement.addEventListener('ended', () => {
          setPlayingAudio(null);
          setAudioProgress(prev => ({ ...prev, [answerId]: { currentTime: 0, percentage: 0 } }));
        });

        audioElement.addEventListener('error', () => {
          setAudioLoading(prev => ({ ...prev, [answerId]: false }));
          console.error('Error loading audio:', audioUrl);
        });
      }
      
      try {
        await audioRefs.current[answerId].play();
        setPlayingAudio(answerId);
        setAudioLoading(prev => ({ ...prev, [answerId]: false }));
      } catch (error) {
        console.error('Error playing audio:', error);
        setAudioLoading(prev => ({ ...prev, [answerId]: false }));
      }
    }
  };

  const handleSeek = (answerId, percentage) => {
    const audioElement = audioRefs.current[answerId];
    if (audioElement && audioDuration[answerId] && isFinite(audioDuration[answerId])) {
      const newTime = (percentage / 100) * audioDuration[answerId];
      audioElement.currentTime = newTime;
    }
  };

  const handleSkip = (answerId, seconds) => {
    const audioElement = audioRefs.current[answerId];
    if (audioElement && isFinite(audioElement.currentTime)) {
      const newTime = Math.max(0, Math.min(audioElement.currentTime + seconds, audioDuration[answerId] || 0));
      audioElement.currentTime = newTime;
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Just now';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      if (isNaN(date.getTime())) return 'Just now';
      return date.toLocaleString();
    } catch (error) {
      console.warn('Error formatting date:', error);
      return 'Just now';
    }
  };

  const handleDownloadAudio = async (audioUrl, answerId) => {
    try {
      // Fetch the audio file as a blob
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Create a meaningful filename
      const date = new Date().toISOString().split('T')[0];
      const questionText = question?.text || 'Answer';
      const safeQuestionText = questionText.substring(0, 30).replace(/[^a-z0-9]/gi, '-');
      link.download = `${safeQuestionText}-${date}.mp3`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading audio:', error);
      alert('Failed to download audio. Please try again.');
    }
  };

  const handleEditStart = (answer) => {
    const currentText = answer.cleanedTranscription || answer.rawTranscription || '';
    setEditingAnswer(answer.id);
    setEditText(currentText);
    setOriginalEditText(currentText); // Store original text for undo
  };

  const handleEditCancel = () => {
    setEditingAnswer(null);
    setEditText('');
    setOriginalEditText(''); // Clear original text
  };

  const handleUndo = () => {
    setEditText(originalEditText);
  };

  const handleEditSave = async (answerId) => {
    if (!editText.trim()) return;
    
    setSaving(true);
    try {
      await fn.updateTranscription({
        storyId,
        answerId,
        transcription: editText.trim()
      });
      
      // Update local state
      setThreads(prevThreads => 
        prevThreads.map(thread => ({
          ...thread,
          originalAnswer: thread.originalAnswer.id === answerId 
            ? { ...thread.originalAnswer, cleanedTranscription: editText.trim() }
            : thread.originalAnswer,
          followups: thread.followups.map(followup => 
            followup.id === answerId 
              ? { ...followup, cleanedTranscription: editText.trim() }
              : followup
          )
        }))
      );
      
      setEditingAnswer(null);
      setEditText('');
      setOriginalEditText(''); // Clear original text after successful save
    } catch (error) {
      console.error('Error updating transcription:', error);
      console.error('Error details:', error.message, error.code, error.details);
      alert(`Failed to save changes: ${error.message || 'Unknown error'}. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const handleAICleanup = async (answerId) => {
    if (!editText.trim()) return;
    
    // Store current text before AI cleanup for undo functionality
    setOriginalEditText(editText);
    
    setCleaningUp(true);
    try {
      const result = await fn.cleanupTranscription({
        storyId,
        questionId,
        answerId,
        rawTranscription: editText.trim()
      });
      
      if (result.data.success) {
        setEditText(result.data.cleanedTranscription);
        
        // Update local state to reflect the cleaned transcription
        setThreads(prevThreads => 
          prevThreads.map(thread => ({
            ...thread,
            originalAnswer: thread.originalAnswer.id === answerId 
              ? { ...thread.originalAnswer, cleanedTranscription: result.data.cleanedTranscription }
              : thread.originalAnswer,
            followups: thread.followups.map(followup => 
              followup.id === answerId 
                ? { ...followup, cleanedTranscription: result.data.cleanedTranscription }
                : followup
            )
          }))
        );
      } else {
        throw new Error(result.data.error || 'AI cleanup failed');
      }
    } catch (error) {
      console.error('Error with AI cleanup:', error);
      alert('Failed to clean up transcription. Please try again.');
    } finally {
      setCleaningUp(false);
    }
  };

  const handleGenerateFollowup = async (answer) => {
    try {
      // Get the transcript - try multiple possible fields
      const transcript = answer.finalAnswer || answer.cleanedTranscription || answer.rawTranscription;
      
      if (!transcript) {
        alert('No transcript available to generate follow-up question.');
        return;
      }

      const result = await fn.generateSimpleFollowupQuestion({
        transcript: transcript,
        originalQuestion: question.text
      });

      if (result.data.success && result.data.question) {
        // Show the generated follow-up question in a modal
        setGeneratedFollowup(result.data.question);
        setShowFollowupModal(true);
      } else {
        alert('Failed to generate follow-up question. Please try again.');
      }
    } catch (error) {
      console.error('Error generating follow-up question:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details
      });
      alert(`Failed to generate follow-up question: ${error.message || 'Unknown error'}. Please try again.`);
    }
  };

  const handleAddFollowupAsStoryQuestion = async () => {
    if (!generatedFollowup || !currentUser || !storyId) {
      alert('Unable to add question. Please try again.');
      return;
    }

    try {
      // Get current questions to find the insertion point
      const questionsQuery = query(
        collection(db, 'stories', storyId, 'questions'),
        orderBy('order')
      );
      const questionsSnapshot = await getDocs(questionsQuery);
      const questions = questionsSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));

      // Find current question and determine insertion order
      const currentQuestionIndex = questions.findIndex(q => q.id === questionId);
      const insertAfterOrder = currentQuestionIndex >= 0 ? questions[currentQuestionIndex].order : 0;

      // Use transaction to ensure order consistency
      await runTransaction(db, async (transaction) => {
        const newQuestionId = `question_${Date.now()}`;
        const newQuestionRef = doc(db, 'stories', storyId, 'questions', newQuestionId);

        // Update order of questions that come after the current one
        const questionsToUpdate = questions.slice(currentQuestionIndex + 1);
        questionsToUpdate.forEach(question => {
          const questionRef = doc(db, 'stories', storyId, 'questions', question.id);
          transaction.update(questionRef, {
            order: question.order + 1
          });
        });
        
        // Insert the new question right after the current one
        transaction.set(newQuestionRef, {
          id: newQuestionId,
          text: generatedFollowup,
          order: insertAfterOrder + 1,
          storyId: storyId,
          userId: currentUser.uid,
          createdAt: serverTimestamp(),
          hasAnswer: false
        });
      });

      // Close modal and show success message
      setShowFollowupModal(false);
      setGeneratedFollowup(null);
      alert('Follow-up question added to your story successfully!');
      
    } catch (error) {
      console.error('Error adding follow-up question to story:', error);
      alert('Failed to add follow-up question to your story. Please try again.');
    }
  };

  const handleReRecord = () => {
    // Call the parent AudioRecorder's re-record callback to properly reset state
    if (onReRecord) {
      onReRecord();
    }
  };

  // Photo handling functions
  const handlePhotoUpload = async (answerId, updatedPhotos) => {
    try {
      // Update local state with the new photos array
      setPhotos(prev => ({
        ...prev,
        [answerId]: updatedPhotos
      }));

      // Update the answer in Firestore with the new photos
      const answerRef = doc(db, 'stories', storyId, 'questions', questionId, 'answers', answerId);
      await updateDoc(answerRef, {
        photos: updatedPhotos
      });

      // Don't close modal automatically - let user close when done
    } catch (error) {
      console.error('Error updating photos:', error);
      alert('Failed to update photos. Please try again.');
    }
  };

  const handleRemovePhoto = async (answerId, photoIndex) => {
    try {
      const currentPhotos = photos[answerId] || [];
      const newPhotos = currentPhotos.filter((_, index) => index !== photoIndex);
      
      setPhotos(prev => ({
        ...prev,
        [answerId]: newPhotos
      }));

      // Update the answer in Firestore
      const answerRef = doc(db, 'stories', storyId, 'questions', questionId, 'answers', answerId);
      await updateDoc(answerRef, {
        photos: newPhotos
      });
    } catch (error) {
      console.error('Error removing photo:', error);
      alert('Failed to remove photo. Please try again.');
    }
  };

  const handleMovePhoto = async (answerId, photoIndex, direction) => {
    const currentPhotos = photos[answerId] || [];
    if ((direction === 'up' && photoIndex === 0) || 
        (direction === 'down' && photoIndex === currentPhotos.length - 1)) {
      return;
    }
    
    try {
      const newPhotos = [...currentPhotos];
      const newIndex = direction === 'up' ? photoIndex - 1 : photoIndex + 1;
      [newPhotos[photoIndex], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[photoIndex]];
      
      setPhotos(prev => ({
        ...prev,
        [answerId]: newPhotos
      }));

      // Update the answer in Firestore
      const answerRef = doc(db, 'stories', storyId, 'questions', questionId, 'answers', answerId);
      await updateDoc(answerRef, {
        photos: newPhotos
      });
    } catch (error) {
      console.error('Error moving photo:', error);
      alert('Failed to move photo. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="memory-stream-card">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="memory-stream-card text-center py-8">
        <p className="text-gray-500">No answers yet for this question.</p>
        <p className="text-sm text-gray-400 mt-2">Record your first answer to get started!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {threads.map((thread, threadIndex) => (
        <div key={thread.originalAnswer.id} className="memory-stream-card p-3 md:p-6">
          {/* Transcription */}
          <div className="pl-0">
            {/* Audio Player (mobile: full width at top) */}
            {thread.originalAnswer.audioUrl && (
              <div className="mb-4 p-4 md:p-3 bg-white rounded-xl border border-gray-200">
                {/* Title */}
                <div className="text-sm font-medium text-gray-700 mb-3 text-center md:hidden">Audio Recording</div>
                
                {/* Main Controls */}
                <div className="flex items-center justify-center space-x-4 mb-3">
                  {/* Skip Back 15s */}
                  <button
                    onClick={() => handleSkip(thread.originalAnswer.id, -15)}
                    disabled={audioLoading[thread.originalAnswer.id] || !audioDuration[thread.originalAnswer.id]}
                    className="flex items-center justify-center w-10 h-10 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 relative"
                    title="Skip back 15 seconds"
                  >
                    <div className="flex items-center">
                      <span className="text-xs font-bold">15</span>
                      <svg className="h-4 w-4 ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </div>
                  </button>
                  
                  {/* Play/Pause */}
                  <button
                    onClick={() => handlePlayPause(thread.originalAnswer.audioUrl, thread.originalAnswer.id)}
                    disabled={audioLoading[thread.originalAnswer.id]}
                    className="flex items-center justify-center w-12 h-12 bg-gray-900 text-white rounded-full hover:bg-gray-800 disabled:opacity-50 transition-colors shadow-sm"
                  >
                    {audioLoading[thread.originalAnswer.id] ? (
                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : playingAudio === thread.originalAnswer.id ? (
                      <PauseIcon className="h-5 w-5" />
                    ) : (
                      <PlayIcon className="h-5 w-5 ml-0.5" />
                    )}
                  </button>
                  
                  {/* Skip Forward 15s */}
                  <button
                    onClick={() => handleSkip(thread.originalAnswer.id, 15)}
                    disabled={audioLoading[thread.originalAnswer.id] || !audioDuration[thread.originalAnswer.id]}
                    className="flex items-center justify-center w-10 h-10 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30"
                    title="Skip forward 15 seconds"
                  >
                    <div className="flex items-center">
                      <svg className="h-4 w-4 mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="text-xs font-bold">15</span>
                    </div>
                  </button>
                </div>
                
                {/* Progress Bar */}
                <div className="space-y-2">
                  <div 
                    className="w-full bg-gray-300 rounded-full h-1 cursor-pointer"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const percentage = ((e.clientX - rect.left) / rect.width) * 100;
                      handleSeek(thread.originalAnswer.id, percentage);
                    }}
                  >
                    <div 
                      className="bg-gray-900 h-1 rounded-full transition-all duration-100"
                      style={{ width: `${audioProgress[thread.originalAnswer.id]?.percentage || 0}%` }}
                    ></div>
                  </div>
                  
                  {/* Time Display */}
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{formatTime(audioProgress[thread.originalAnswer.id]?.currentTime || 0)}</span>
                    <span>{formatTime(audioDuration[thread.originalAnswer.id] || 0)}</span>
                  </div>
                </div>
                
                {/* Download Button */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleDownloadAudio(thread.originalAnswer.audioUrl, thread.originalAnswer.id)}
                    className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-gray-50 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    <span className="text-base font-medium">Download Audio</span>
                  </button>
                </div>
              </div>
            )}

            {/* Answer Title (mobile: below audio player) */}
            <div className="mb-3">
              <h4 className="font-serif font-medium text-gray-900 text-lg">
                Answer {threads.length > 1 ? `#${threadIndex + 1}` : ''}
              </h4>
            </div>

            {/* Editable Transcription */}
            <div className="mb-3">
              {editingAnswer === thread.originalAnswer.id ? (
                <div className="space-y-3">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className={`w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${calculateTextareaHeight(editText)} text-gray-700 text-[15px] md:text-base leading-relaxed`}
                    placeholder="Edit your answer..."
                    disabled={saving || cleaningUp}
                  />
                  <div className="flex flex-col space-y-3">
                    <button
                      onClick={() => handleEditSave(thread.originalAnswer.id)}
                      disabled={saving || cleaningUp || !editText.trim()}
                      className="flex items-center justify-center space-x-2 w-full px-5 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-base font-medium"
                    >
                      <CheckIcon className="h-5 w-5" />
                      <span>{saving ? 'Saving...' : 'Save'}</span>
                    </button>
                    <button
                      onClick={() => handleAICleanup(thread.originalAnswer.id)}
                      disabled={saving || cleaningUp || !editText.trim()}
                      className="flex items-center justify-center space-x-2 w-full px-5 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-base font-medium"
                    >
                      <SparklesIcon className="h-5 w-5" />
                      <span>{cleaningUp ? 'Cleaning...' : 'AI Cleanup'}</span>
                    </button>
                    {originalEditText && originalEditText !== editText && (
                      <button
                        onClick={handleUndo}
                        disabled={saving || cleaningUp}
                        className="flex items-center justify-center space-x-2 w-full px-5 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-base font-medium"
                        title="Undo AI cleanup changes"
                      >
                        <ArrowUturnLeftIcon className="h-5 w-5" />
                        <span>Undo</span>
                      </button>
                    )}
                    <button
                      onClick={handleEditCancel}
                      disabled={saving || cleaningUp}
                      className="flex items-center justify-center space-x-2 w-full px-5 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 transition-colors text-base font-medium"
                    >
                      <XMarkIcon className="h-5 w-5" />
                      <span>Cancel</span>
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-700 text-[15px] md:text-base leading-relaxed mb-2 whitespace-pre-wrap">
                  {thread.originalAnswer.cleanedTranscription || thread.originalAnswer.rawTranscription}
                </p>
              )}
            </div>

            {/* Edit and Action Controls */}
            {userAccessLevel !== 'viewer' && (
              <div className="space-y-2">
                {/* Edit Controls */}
                {editingAnswer !== thread.originalAnswer.id && (
                  <div className="space-y-2">
                    <button
                      onClick={() => handleEditStart(thread.originalAnswer)}
                      className="flex items-center justify-center space-x-2 px-3 md:px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors w-full"
                    >
                      <PencilIcon className="h-4 w-4" />
                      <span className="text-sm font-medium">Edit Answer</span>
                    </button>
                    
                    {/* Generate Follow-up Question Button */}
                    {!thread.originalAnswer.isFollowup && userAccessLevel === 'owner' && (
                      <button
                        onClick={() => handleGenerateFollowup(thread.originalAnswer)}
                        className="flex items-center justify-center space-x-2 px-3 md:px-4 py-2 bg-[#C87400] text-white rounded-md hover:bg-[#B06600] transition-colors w-full"
                      >
                        <SparklesIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">Generate Follow-up Question</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Photos Section */}
            <div className="mt-6 border-t border-gray-300 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-sm font-medium text-gray-900">Photos</h5>
                {userAccessLevel !== 'viewer' && (
                  <button
                    onClick={() => setShowPhotoModal(thread.originalAnswer.id)}
                    className="flex items-center space-x-1 px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    <ArrowUpTrayIcon className="h-3 w-3" />
                    <span>Add Photo</span>
                  </button>
                )}
              </div>
              
              <p className="text-xs text-gray-500 mb-3">
                💡 These photos will appear at the end of your chapter in your book
              </p>
              
              {photos[thread.originalAnswer.id]?.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  {photos[thread.originalAnswer.id].map((photo, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={photo.url}
                        alt={`Answer item ${index + 1}`}
                        className="w-full h-32 sm:h-36 object-cover rounded-lg border border-gray-200"
                      />
                      
                      {/* Photo controls */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="flex space-x-2">
                          {/* Move up button */}
                          {index > 0 && (
                            <button
                              onClick={() => handleMovePhoto(thread.originalAnswer.id, index, 'up')}
                              className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-blue-600 transition-colors shadow-lg"
                              title="Move up"
                            >
                              <ChevronUpIcon className="h-4 w-4" />
                            </button>
                          )}
                          
                          {/* Move down button */}
                          {index < photos[thread.originalAnswer.id].length - 1 && (
                            <button
                              onClick={() => handleMovePhoto(thread.originalAnswer.id, index, 'down')}
                              className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-blue-600 transition-colors shadow-lg"
                              title="Move down"
                            >
                              <ChevronDownIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Delete button */}
                      <button
                        onClick={() => handleRemovePhoto(thread.originalAnswer.id, index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                        title="Remove photo"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <ArrowUpTrayIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No photos added yet</p>
                  <p className="text-xs text-gray-400 mt-1">Click "Add Photo" to attach images to this answer</p>
                </div>
              )}
            </div>
            
            {/* Re-Record Button - Placed at bottom after photos for safety */}
            {userAccessLevel !== 'viewer' && editingAnswer !== thread.originalAnswer.id && (
              <div className="mt-6 pt-6 border-t border-gray-300">
                <button
                  onClick={handleReRecord}
                  className="flex items-center justify-center space-x-2 px-3 md:px-4 py-2 bg-orange-50 text-orange-700 border border-orange-200 rounded-md hover:bg-orange-100 transition-colors w-full"
                >
                  <MicrophoneIcon className="h-4 w-4" />
                  <span className="text-sm font-medium">Re-Record Answer</span>
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">⚠️ This will clear your current recording</p>
              </div>
            )}
          </div>

          {/* Follow-up Answers */}
          {thread.followups.length > 0 && (
            <div className="mt-4 space-y-3">
              <h5 className="text-sm font-medium text-gray-600 flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                Follow-up Responses ({thread.followups.length})
              </h5>
              
              {thread.followups.map((followup, followupIndex) => {
                return (
                <div key={followup.id} className="pl-2 ml-2 md:pl-4 md:ml-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Follow-up #{followupIndex + 1}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDate(followup.createdAt)}
                    </span>
                  </div>
                  
                  {/* Follow-up Question */}
                  {followup.followupQuestion ? (
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm font-medium text-blue-900 mb-1">Question:</p>
                      <p className="text-sm text-blue-800">{followup.followupQuestion}</p>
                    </div>
                  ) : (
                    <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm font-medium text-red-900 mb-1">DEBUG: Missing follow-up question</p>
                      <p className="text-sm text-red-800">followupQuestion field is: {JSON.stringify(followup.followupQuestion)}</p>
                    </div>
                  )}
                  
                  <p className="text-gray-600 text-sm mb-2 whitespace-pre-wrap">
                    {followup.cleanedTranscription || followup.rawTranscription}
                  </p>
                  
                  {/* Followup Photos Section */}
                  <div className="mt-4 pt-3 border-t border-gray-300">
                    <div className="flex items-center justify-between mb-3">
                      <h6 className="text-xs font-medium text-gray-700">Photos</h6>
                      <button
                        onClick={() => setShowPhotoModal(followup.id)}
                        className="flex items-center space-x-1 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
                      >
                        <ArrowUpTrayIcon className="h-3 w-3" />
                        <span>Add</span>
                      </button>
                    </div>
                    
                    {photos[followup.id]?.length > 0 ? (
                      <div className="grid grid-cols-2 gap-2">
                        {photos[followup.id].map((photo, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={photo.url}
                              alt={`Follow-up item ${index + 1}`}
                              className="w-full h-20 object-cover rounded border border-gray-200"
                            />
                            
                            {/* Photo controls */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <div className="flex space-x-1">
                                {/* Move up button */}
                                {index > 0 && (
                                  <button
                                    onClick={() => handleMovePhoto(followup.id, index, 'up')}
                                    className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-blue-600 transition-colors shadow-lg"
                                    title="Move up"
                                  >
                                    <ChevronUpIcon className="h-3 w-3" />
                                  </button>
                                )}
                                
                                {/* Move down button */}
                                {index < photos[followup.id].length - 1 && (
                                  <button
                                    onClick={() => handleMovePhoto(followup.id, index, 'down')}
                                    className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-blue-600 transition-colors shadow-lg"
                                    title="Move down"
                                  >
                                    <ChevronDownIcon className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                            
                            {/* Delete button */}
                            <button
                              onClick={() => handleRemovePhoto(followup.id, index)}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                              title="Remove photo"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-400 bg-gray-50 rounded border border-dashed border-gray-200">
                        <ArrowUpTrayIcon className="h-5 w-5 mx-auto mb-1" />
                        <p className="text-xs">No photos</p>
                      </div>
                    )}
                  </div>
                </div>
              )
              })}
            </div>
          )}
        </div>
      ))}

      {/* Follow-up Question Modal */}
      {showFollowupModal && generatedFollowup && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
          style={{ 
            zIndex: 999999, 
            position: 'fixed',
            isolation: 'isolate',
            WebkitTransform: 'translateZ(0)'
          }}
        >
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto" style={{ zIndex: 1000000 }}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Generated Follow-up Question</h3>
                <button
                  onClick={() => setShowFollowupModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-2">AI Generated Question:</p>
                <p className="text-blue-800 text-base leading-relaxed">{generatedFollowup}</p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleAddFollowupAsStoryQuestion}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Add as New Question
                </button>
                <button
                  onClick={() => setShowFollowupModal(false)}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Photo Upload Modal */}
      {showPhotoModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{
            zIndex: 999999,
            position: 'fixed',
            isolation: 'isolate',
            WebkitTransform: 'translateZ(0)'
          }}
          onClick={() => setShowPhotoModal(null)}
        >
          <div
            className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            style={{ zIndex: 1000000 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Add Photos</h3>
              <button
                onClick={() => setShowPhotoModal(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <PhotoUpload
                storyId={storyId}
                questionId={questionId}
                answerId={showPhotoModal}
                existingPhotos={photos[showPhotoModal] || []}
                onPhotosUpdate={(updatedPhotos) => handlePhotoUpload(showPhotoModal, updatedPhotos)}
              />
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowPhotoModal(null)}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AnswerThread;
